import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemChip } from './pills';
import { ListStateShell } from './ListStateShell';
import { useListFetch } from './useListFetch';
import { KpiRow } from './portfolio/KpiRow';
import { RentRollCard } from './portfolio/RentRollCard';
import { WhatsAppTicketsCard } from './portfolio/WhatsAppTicketsCard';
import { LeasesExpiringCard } from './portfolio/LeasesExpiringCard';
import { UnitsGridCard } from './portfolio/UnitsGridCard';
import { UnitDrillInOverlay } from './portfolio/UnitDrillInOverlay';
import { PortfolioEmptyState } from './portfolio/PortfolioEmptyState';
import { PropertyImportFlow } from './portfolio/PropertyImportFlow';

/**
 * PortfolioTab — real-estate module (realestate-dashboard-prd.md §0). ALL SIX sections are REAL now,
 * each composed from its own file under ./portfolio/ (the section-component pattern): the KPI row
 * (Part 3), the rent roll (Part 1), the "From WhatsApp" work-order card (Part 2), leases-expiring-soon
 * (Part 4) and the units grid + §0.4 drill-in (Part 5). Nothing on this page is mock any more — the
 * "Proposal" banner and the per-section mock CardBadges are GONE (§0.2's per-section-removal rule, now
 * complete for every section).
 *
 * §0.5 — the whole-dashboard first-run/empty state: zero Property rows → none of the sections render
 * in "populated-but-zero" ghost form; ONE first-run panel takes their place. Gated on its OWN fetch
 * (GET /api/properties), so a fetch FAILURE never gets misread as "you have none" (ListStateShell's
 * surface-owns-its-empty contract). The "Demo data" chip is an INDEPENDENT fact from the empty state
 * (rejected-alternative note, §0.4) — it shows whenever seeded Property rows exist, whether or not
 * real ones do too, never merged into one banner with the first-run copy.
 */

const DEMO_PREFIX = 'DEMO — '; // the existing seeded-marker convention (scripts/seed_crm_demo.ts / seed_rentroll_demo.ts)

interface PropertyRow { id: string; name: string }

export const PortfolioTab: React.FC<{ onConnectWhatsApp?: () => void; onNavigateToContacts?: () => void }> = ({ onConnectWhatsApp, onNavigateToContacts }) => {
  const { t } = useTranslation('common');
  // The ONE page-level "WhatsApp not connected" fact (§1.3 point 3 / AC1.3a) — rendered once here, not
  // per-row and not per-card, shared by BOTH the rent-roll reminder (Part 1) AND the leases renewal
  // nudge (Part 4) AND Part 5's drill-in reminder. Uses the SAME connected definition the send path
  // uses (credential exists AND status 'connected').
  const [waConnected, setWaConnected] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/whatsapp/connection-status')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setWaConnected(!!d.connected && d.status === 'connected'); })
      .catch(() => { if (alive) setWaConnected(null); }); // unknown → no banner (don't assert a fault we can't see)
    return () => { alive = false; };
  }, []);

  // §0.5 — the whole-dashboard empty gate. Own fetch, own status: loading→skeleton, error→retry,
  // ready+zero→the first-run panel, ready+nonzero→the real dashboard below.
  const [refreshKey, setRefreshKey] = useState(0);
  const propertiesList = useListFetch<PropertyRow[]>('/api/properties', { refreshKey });
  const properties = propertiesList.data ?? [];
  const demoPresent = properties.some((p) => p.name.startsWith(DEMO_PREFIX));

  const [openUnitId, setOpenUnitId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // The ONE dashboard-refetch signal (brief §4). useListFetch silently revalidates on a refreshKey
  // bump WITHOUT remounting, so a bump alone would not re-run the section cards' own fetches; keying
  // the populated subtree on refreshKey remounts every card, so each refetches after any setup write.
  const reload = () => setRefreshKey((k) => k + 1);

  return (
    <div className="relative flex-1 overflow-y-auto p-4" data-testid="portfolio-tab">
      {/* Independent provenance marker (§0.4 rejected-alternatives note) — never merged with the
          first-run empty copy below; shows whenever seeded rows exist, empty state or not. */}
      {demoPresent && (
        <div className="absolute top-4 [inset-inline-end:1rem] z-10">
          <SystemChip label={t('portfolio.demoData')} tone="muted" testId="portfolio-demo-data-chip" />
        </div>
      )}

      <ListStateShell status={propertiesList.status} testIdBase="portfolio" onRetry={propertiesList.retry}>
        {properties.length === 0 ? (
          <PortfolioEmptyState onCreated={reload} onImport={() => setImportOpen(true)} />
        ) : (
          <React.Fragment key={refreshKey}>
            {/* Setup actions — the money-flow onboarding wedge (brief §1) stays reachable even once a
                property exists, so a user can add a 2nd building (bulk) or more units. */}
            <div className="flex items-center justify-end mb-4">
              <button type="button" data-testid="portfolio-import-building" onClick={() => setImportOpen(true)}
                className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold">
                {t('portfolio.setup.importBuilding')}
              </button>
            </div>

            {/* ONE page-level WhatsApp-not-connected banner (WaConversationsGrid pattern, §1.3 point 3 /
                AC1.3a) — shared by the rent roll AND the leases renewal nudge, rendered once. */}
            {waConnected === false && (
              <div data-testid="portfolio-wa-not-connected-banner" role="status"
                className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg border border-border-neutral bg-surface text-[12px] text-text-strong">
                <span aria-hidden>⚠</span>
                <span>{t('portfolio.whatsappNotConnected')}</span>
                {onConnectWhatsApp && (
                  <button type="button" data-testid="portfolio-wa-connect-link"
                    className="inline-flex items-center px-2 underline font-semibold text-primary" onClick={onConnectWhatsApp}>
                    {t('portfolio.connect')}
                  </button>
                )}
              </div>
            )}

            {/* Part 3 — REAL KPI row (§3.1's account-wide "morning view", above every property card). */}
            <KpiRow />

            <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 mb-4">
              {/* Part 1 — REAL rent roll, composed from its own section component. */}
              <RentRollCard />

              <div className="flex flex-col gap-4">
                {/* Part 2 — REAL "From WhatsApp" work-order card. */}
                <WhatsAppTicketsCard />

                {/* Part 4 — REAL leases expiring soon, composed from its own section component. */}
                <LeasesExpiringCard />
              </div>
            </div>

            {/* Part 5 — REAL units grid, one card per Property, composed from its own section component. */}
            <UnitsGridCard onOpenUnit={setOpenUnitId} onChanged={reload} />
          </React.Fragment>
        )}
      </ListStateShell>

      {/* §0.4 — the unit drill-in overlay, a sibling of everything above (agent-console's
          ticketCreatePrefill shape: parent holds the id, the overlay renders alongside). onChanged
          refetches the sections behind it after a lease is added from the drill-in. */}
      {openUnitId && (
        <UnitDrillInOverlay unitId={openUnitId} onClose={() => setOpenUnitId(null)} onNavigateToContacts={onNavigateToContacts} onChanged={reload} />
      )}

      {/* The bulk-import onboarding wedge (brief §1) — a sibling overlay; on a completed import it
          refetches the dashboard so the new building's units + rent roll appear in-app. */}
      {importOpen && (
        <PropertyImportFlow onClose={() => setImportOpen(false)} onImported={reload} />
      )}
    </div>
  );
};
